import React, { useState } from "react";

import { Plus } from "lucide-react";

import {
  Checkbox,
  EditableRow,
  Field,
  inputClass,
  OriginPill,
  Panel,
  Pill,
  RowActions,
  smallButton,
} from "./parts";
import { S } from "./strings";
import { isCustomisedFromDefault, itemOrigin } from "./templateDiff";
import { useAppDispatch } from "../../Store/Hooks";
import {
  addTemplateQuestion,
  moveTemplateItem,
  PlanTemplate,
  removeTemplateItem,
  resetTemplateItem,
  updateTemplateQuestion,
} from "../../Store/Reducers/AccountPlanTemplateSlice";

interface Props {
  template: PlanTemplate;
  defaults: PlanTemplate;
  isDefaultScope: boolean;
  liveKeys: Set<string>;
}

const QuestionsPanel: React.FC<Props> = ({
  template,
  defaults,
  isDefaultScope,
  liveKeys,
}) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<string | null>(
    template.questions[0]?.key ?? null
  );
  const count = template.questions.length;
  // A question can inform any section with content (not the account header).
  const sections = template.sections.filter(s => !s.system);
  const informable = sections.map(s => s.key);

  const add = () => {
    const key = `custom-q-${Date.now()}`;
    dispatch(addTemplateQuestion({ key }));
    setOpen(key);
  };

  return (
    <Panel title={S.questionsTitle} hint={S.questionsHint}>
      <ol className="m-0 list-none p-0">
        {template.questions.map((q, i) => {
          const origin = itemOrigin(isDefaultScope, defaults, "questions", q);
          const informs = q.informs ?? [];
          const sub = [
            q.custom ? S.customQuestion : S.builtinQuestion,
            informs.length ? S.informsMeta(informs.length) : "",
          ]
            .filter(Boolean)
            .join(" · ");
          // Kept in briefing order, as the server stores it.
          const setInforms = (key: string, on: boolean) => {
            const want = new Set(informs);
            if (on) want.add(key);
            else want.delete(key);
            update({ informs: informable.filter(k => want.has(k)) });
          };
          const update = (
            patch: Parameters<typeof updateTemplateQuestion>[0]["patch"]
          ) => dispatch(updateTemplateQuestion({ key: q.key, patch }));
          return (
            <EditableRow
              key={q.key}
              index={i + 1}
              title={q.text || S.untitledQuestion}
              dim={!q.enabled}
              sub={sub}
              open={open === q.key}
              onToggle={() => setOpen(open === q.key ? null : q.key)}
              tags={
                <>
                  {!q.enabled && <Pill tone="faint">{S.off}</Pill>}
                  {!liveKeys.has(q.key) && isDefaultScope && (
                    <Pill tone="amber">{S.newItem}</Pill>
                  )}
                  <OriginPill origin={origin} />
                </>
              }
            >
              <Field label={S.questionLabel} htmlFor={`q-text-${q.key}`}>
                <textarea
                  id={`q-text-${q.key}`}
                  rows={2}
                  value={q.text}
                  placeholder={S.questionPlaceholder}
                  onChange={e => update({ text: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <fieldset className="grid gap-1.5">
                <legend className="text-xs font-semibold text-gray-700">
                  {S.informsLabel}
                </legend>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {sections.map(sec => (
                    <Checkbox
                      key={sec.key}
                      id={`q-informs-${q.key}-${sec.key}`}
                      label={`${sec.title || S.untitledSection}${
                        sec.enabled ? "" : ` (${S.hidden.toLowerCase()})`
                      }`}
                      checked={informs.indexOf(sec.key) >= 0}
                      onChange={on => setInforms(sec.key, on)}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500">{S.informsHelp}</p>
              </fieldset>
              <RowActions
                canUp={i > 0}
                canDown={i < count - 1}
                onMove={by =>
                  dispatch(
                    moveTemplateItem({ kind: "questions", key: q.key, by })
                  )
                }
                onReset={
                  !isDefaultScope &&
                  isCustomisedFromDefault(defaults, "questions", q)
                    ? () =>
                        dispatch(
                          resetTemplateItem({ kind: "questions", key: q.key })
                        )
                    : undefined
                }
                onRemove={
                  q.custom
                    ? () => {
                        dispatch(
                          removeTemplateItem({ kind: "questions", key: q.key })
                        );
                        setOpen(null);
                      }
                    : undefined
                }
              >
                <Checkbox
                  id={`q-on-${q.key}`}
                  label={S.askThis}
                  checked={q.enabled}
                  onChange={enabled => update({ enabled })}
                />
              </RowActions>
            </EditableRow>
          );
        })}
      </ol>
      <div className="border-t border-dashed border-gray-300 px-4 py-3">
        <button type="button" className={smallButton} onClick={add}>
          <span className="inline-flex items-center gap-1">
            <Plus size={14} />
            {S.addQuestion}
          </span>
        </button>
      </div>
    </Panel>
  );
};

export default QuestionsPanel;
